/**
 * Client-side seal orchestration (E4-S5).
 *
 * The browser performs the actual cryptographic seal. Server NEVER sees
 * plaintext content and NEVER sees the plaintext CEK. The flow is:
 *
 *   1. Generate a fresh 32-byte CEK (`crypto.cek.generateCek`).
 *   2. AES-256-GCM-encrypt the content (video bytes / audio bytes / letter
 *      UTF-8) with the CEK.
 *   3. Look up the recipient's public key:
 *        a. If recipient is a placeholder OR `users.public_key IS NULL`
 *           → wrap CEK to the OWNER's pubkey, set delivery_key_holder='owner'.
 *        b. Else → wrap CEK to recipient pubkey via HPKE,
 *                  set delivery_key_holder='recipient'.
 *   4. Wrap the SAME CEK with the vault DEK (continuity envelope).
 *      The vault DEK is fetched from the server over TLS — server can see
 *      it briefly per ENCRYPTION-ARCHITECTURE.md §3 (KMS-fronted, audited).
 *      This is the documented compromise for V1.
 *   5. POST /api/messages/:id/seal with both ciphertexts. Server runs the
 *      atomic CAS (draft → sealed) and enqueues the Polygon anchor.
 *
 * On non-letter messages the AES-GCM-encrypted blob is uploaded to R2
 * BEFORE this function runs (via the presigned URL flow). This module only
 * handles the CEK wrapping + the seal API call.
 *
 * IMPORTANT: this module zeroizes the CEK reference after both wraps
 * complete. JS GC may keep copies but we drop the only known reference.
 */
// Import from the specific crypto modules, NOT the barrel (../../crypto/index.js):
// the barrel re-exports server-only modules (pii/local-kms/shamir) that pull
// node:util/node:crypto, which break the browser bundle when this client seal
// path is vendored into the member app. cek.ts + keys.ts are WebCrypto-based and
// browser-safe (the recipient app already bundles them). Same functions, no
// behavior change.
import { generateCek, wrapCekForRecipient, wrapCekForVaultDek, } from '../../crypto/cek.js';
import { importPublicKeyFromServer } from '../../crypto/keys.js';
/**
 * Encrypt content client-side with an AES-256-GCM CEK and return the
 * ciphertext + a fresh 12-byte IV.
 * The caller uploads the ciphertext (ct only) to R2 for media messages and
 * persists the IV separately via the finalize call (media_files.iv); for
 * letter messages it sends (iv, ct) with the seal request. The IV is NEVER
 * prepended to the R2 object.
 */
export async function encryptContentWithCek(args) {
    const subtle = getSubtle();
    const iv = randomBytes(12);
    const key = await subtle.importKey('raw', toAb(args.cek), { name: 'AES-GCM' }, false, ['encrypt']);
    const aad = new TextEncoder().encode(args.messageId);
    const ct = await subtle.encrypt({ name: 'AES-GCM', iv: toAb(iv), additionalData: toAb(aad), tagLength: 128 }, key, toAb(args.plaintext));
    return { ciphertext: new Uint8Array(ct), iv };
}
/**
 * Prove, before sealing, that what we are about to store actually opens.
 *
 * A sealed message is not read again for years — possibly decades. Until this
 * existed, nothing ever verified that the ciphertext, IV and CEK we produced
 * were a consistent triple: we encrypted, uploaded, and declared success. A
 * silent fault in any of the three would surface on the one day it must not,
 * in front of the one person who cannot be told "sorry, try again".
 *
 * So we decrypt what we just encrypted, with the same key and AAD the
 * recipient's device will use, and compare byte for byte. It costs one
 * in-memory AES-GCM pass and it converts "we think it is sealed" into "we
 * watched it open". If it fails, the seal is ABORTED — an unopenable message
 * that believes it is safe is far worse than a failed save the person can retry.
 *
 * Returns the SHA-256 of the CIPHERTEXT, which is recorded server-side so an
 * integrity sweep can later prove the stored bytes are still the bytes that
 * passed this test — without any key, and without decrypting anything.
 */
export async function selfTestCiphertext(args) {
    const subtle = getSubtle();
    const key = await subtle.importKey('raw', toAb(args.cek), { name: 'AES-GCM' }, false, ['decrypt']);
    const aad = new TextEncoder().encode(args.messageId);
    let roundTripped;
    try {
        const plain = await subtle.decrypt({ name: 'AES-GCM', iv: toAb(args.iv), additionalData: toAb(aad), tagLength: 128 }, key, toAb(args.ciphertext));
        roundTripped = new Uint8Array(plain);
    }
    catch (err) {
        throw new Error('Self-test failed: this message did not decrypt with its own key, so it was NOT sealed. ' +
            'Nothing was stored that could not be opened. Please try recording again.');
    }
    if (roundTripped.byteLength !== args.plaintext.byteLength) {
        throw new Error('Self-test failed: the decrypted message was a different size. It was NOT sealed.');
    }
    // Constant-time-ish compare. Length is already equal; accumulate differences
    // rather than returning early so this does not leak position via timing.
    let diff = 0;
    for (let i = 0; i < roundTripped.length; i++)
        diff |= roundTripped[i] ^ args.plaintext[i];
    roundTripped.fill(0);
    if (diff !== 0) {
        throw new Error('Self-test failed: the decrypted message did not match what was recorded. It was NOT sealed.');
    }
    const digest = await subtle.digest('SHA-256', toAb(args.ciphertext));
    const ciphertextSha256 = [...new Uint8Array(digest)]
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    return { ciphertextSha256, plaintextBytes: args.plaintext.byteLength };
}
/**
 * Resolve which public key to wrap the CEK to. Falls back to the owner's
 * pubkey when the recipient cannot be wrapped (placeholder or missing key).
 */
export function resolveRecipientKey(input) {
    const r = input.recipient;
    if (r === null || r.is_placeholder || r.public_key === null || r.public_key_suite === null) {
        return {
            publicKey: input.owner.public_key,
            publicKeySuite: input.owner.public_key_suite,
            deliveryKeyHolder: 'owner',
        };
    }
    return {
        publicKey: r.public_key,
        publicKeySuite: r.public_key_suite,
        deliveryKeyHolder: 'recipient',
    };
}
/**
 * Top-level orchestrator. Generates a CEK, performs both wraps, posts the
 * seal API. Returns the server's `SealResult`-shaped payload.
 *
 * Caller is expected to have:
 *   - For video/voice : already uploaded the AES-GCM ciphertext blob to R2
 *                       (via `MediaPresignedController` + browser PUT) and
 *                       called `MediaFinalizeController` with metadata.
 *   - For letter      : the CEK ciphertext + 12-byte IV computed here travel
 *                       WITH the seal request and are persisted server-side
 *                       into media_files. The server never re-encrypts — it
 *                       only stores the ciphertext the client sealed.
 */
export async function runSealFlow(args) {
    const ctx = await args.api.fetchSealContext(args.messageId);
    const isLetter = ctx.message.format === 'letter';
    const resolved = resolveRecipientKey({
        recipient: ctx.recipient,
        owner: ctx.owner,
    });
    let cek = generateCek();
    try {
        // 2. Encrypt content.
        const { ciphertext, iv } = await encryptContentWithCek({
            cek,
            plaintext: args.contentPlaintext,
            messageId: args.messageId,
        });
        // Prove it opens BEFORE anything is uploaded or sealed. Doing it here, not
        // after, means a message that fails the test never reaches storage at all.
        const proof = await selfTestCiphertext({
            cek,
            ciphertext,
            iv,
            plaintext: args.contentPlaintext,
            messageId: args.messageId,
        });
        if (args.onCiphertextReady !== undefined) {
            await args.onCiphertextReady({ ciphertext, iv });
        }
        // 3. Wrap to recipient (or owner).
        const recipientPubKey = await importPublicKeyFromServer(resolved.publicKey);
        const cekRecipientWrap = await wrapCekForRecipient(cek, recipientPubKey.publicKey, args.messageId);
        // 4. Wrap to vault DEK.
        const vaultDek = base64ToBytes(ctx.vault_dek_b64);
        if (vaultDek.byteLength !== 32) {
            throw new Error('Vault DEK from server is not 32 bytes; aborting seal.');
        }
        const cekVaultWrap = await wrapCekForVaultDek(cek, vaultDek, args.messageId);
        // 5. POST seal.
        const result = await args.api.postSeal({
            messageId: args.messageId,
            cekRecipientWrap,
            cekVaultWrap,
            recipientPublicKey: resolved.publicKey,
            deliveryKeyHolder: resolved.deliveryKeyHolder,
            cekAlgorithmVersion: ctx.cipher_suite,
            // Letter body travels with the seal; media bodies go to R2 via onCiphertextReady.
            letterCiphertextB64: isLetter ? bytesToBase64(ciphertext) : undefined,
            ivB64: isLetter ? bytesToBase64(iv) : undefined,
            ciphertextSha256: proof.ciphertextSha256,
            plaintextBytes: proof.plaintextBytes,
        });
        // Best-effort zeroize the vault DEK + CEK references.
        vaultDek.fill(0);
        return result;
    }
    finally {
        if (cek !== null) {
            cek.fill(0);
            cek = null;
        }
    }
}
// --- helpers -----------------------------------------------------------------
function getSubtle() {
    const c = globalThis.crypto;
    if (c === undefined || c.subtle === undefined) {
        throw new Error('globalThis.crypto.subtle is unavailable.');
    }
    return c.subtle;
}
function randomBytes(n) {
    const out = new Uint8Array(n);
    const c = globalThis.crypto;
    if (c === undefined || typeof c.getRandomValues !== 'function') {
        throw new Error('globalThis.crypto.getRandomValues is unavailable.');
    }
    c.getRandomValues(out);
    return out;
}
function toAb(view) {
    const copy = new Uint8Array(view.byteLength);
    copy.set(view);
    return copy.buffer;
}
function base64ToBytes(b64) {
    if (typeof globalThis !== 'undefined' && typeof globalThis.atob === 'function') {
        const bin = globalThis.atob(b64);
        const out = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++)
            out[i] = bin.charCodeAt(i);
        return out;
    }
    return new Uint8Array(Buffer.from(b64, 'base64'));
}
function bytesToBase64(bytes) {
    if (typeof globalThis !== 'undefined' && typeof globalThis.btoa === 'function') {
        let bin = '';
        for (let i = 0; i < bytes.length; i++)
            bin += String.fromCharCode(bytes[i]);
        return globalThis.btoa(bin);
    }
    return Buffer.from(bytes).toString('base64');
}
